import React from 'react'
import Skeleton from 'react-loading-skeleton'

import {
  DiningOptionBehavior,
  OrderIdentifier,
  OrderStatusType,
  ThirdPartyDeliveryStatus,
  useGetOrderStatusesQuery
} from 'src/apollo/onlineOrdering'
import { CompletedOrder } from 'src/public/components/online_ordering/types'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { getDisplayBusinessName } from 'src/shared/js/locationUtils'

import { OrderTrackerMap } from 'public/components/default_template/online_ordering/order_tracker/order_tracker_map/OrderTrackerMap'

import DeliveryDriverInfo from './delivery_driver_info/DeliveryDriverInfo'
import DeliveryPhotoButton from './delivery_photo_button/DeliveryPhotoButton'
import OrderStatusBar from './order_status_bar/OrderStatusBar'
import getUnifiedOrderStatus, {
  UnifiedOrderStatus
} from './order_status_bar/statusUtil'
import { TrackerFulfillmentInfo } from './tracker_fulfillment_info/TrackerFulfillmentInfo'

const SIXTY_SECONDS_MS = 60000
const MAX_RETRY = 5
const MILLIS_IN_HOUR: number = 60 * SIXTY_SECONDS_MS

export type OrderTrackerProps = {
  order: CompletedOrder
}

export const OrderTracker = ({ order }: OrderTrackerProps) => {
  const { selectedLocation } = useRestaurant()
  const restaurantName =
    getDisplayBusinessName(order.restaurant.name, selectedLocation?.name) ??
    order.restaurant.name

  const orderIdentifier: OrderIdentifier = {
    orderGuid: order.guid,
    restaurantGuid: order.restaurant.guid,
    diningOptionBehavior: order.diningOptionBehavior,
    isThirdPartyDeliveryServiceOrder: !!order.deliveryServiceInfo
  }

  const [retryCount, setRetryCount] = React.useState(0)

  const {
    data,
    loading: orderStatusInfoLoading,
    stopPolling
  } = useGetOrderStatusesQuery({
    variables: { input: { orderIdentifiers: [orderIdentifier] } },
    pollInterval: SIXTY_SECONDS_MS,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    ssr: false,
    onError: () => {
      const nextRetryCount = retryCount + 1
      if (nextRetryCount >= MAX_RETRY) {
        stopPolling()
      } else {
        setRetryCount(nextRetryCount)
      }
    },
    onCompleted: async () => {
      if (
        data?.orderStatuses.orderStatusesResponse?.at(0)?.deliveryStatus ===
        ThirdPartyDeliveryStatus.OrderDelivered
      ) {
        stopPolling()
      }
    }
  })

  const orderStatusInfo = data?.orderStatuses.orderStatusesResponse?.at(0)
  const curbside =
    order.curbsidePickup?.selected || order.curbsidePickupV2 !== undefined
  const deliveryDriver = orderStatusInfo?.trackingDetails?.deliveryDriver
  const driverInfo = deliveryDriver
    ? {
        driverName: deliveryDriver.name,
        driverNumber: deliveryDriver.phoneNumber,
        driverPhotoUrl: deliveryDriver.profileImageUrl
      }
    : undefined

  const unifiedStatus = getUnifiedOrderStatus(
    orderStatusInfo?.guestOrderStatus ?? OrderStatusType.Received,
    orderStatusInfo?.deliveryStatus ?? null,
    order
  )
  const showDeliveryComponents =
    order.diningOptionBehavior === DiningOptionBehavior.Delivery
  const showDriverInfo =
    showDeliveryComponents &&
    unifiedStatus !== UnifiedOrderStatus.CANCELLED &&
    unifiedStatus !== UnifiedOrderStatus.DELIVERY_FAILED
  const showFulfillmentInfo = ![
    UnifiedOrderStatus.CANCELLED,
    UnifiedOrderStatus.DELIVERY_FAILED
  ].includes(unifiedStatus)

  const isUnderHourAfterDelivery =
    +(orderStatusInfo?.trackingDetails?.actualDeliveryTime ?? Date.now()) +
      +MILLIS_IN_HOUR >
    Date.now()

  const showMap =
    showDeliveryComponents &&
    showDriverInfo &&
    showFulfillmentInfo &&
    isUnderHourAfterDelivery

  let body = (
    <div data-testid='tracker-skeleton'>
      <Skeleton width='100%' height={420} className='trackerSkeleton' />
    </div>
  )

  if (!orderStatusInfoLoading || data) {
    const orderCompletedDate = orderStatusInfo?.lastUpdated
      ? new Date(Date.parse(orderStatusInfo.lastUpdated))
      : undefined

    body = (
      <div>
        <div className='sectionBlock'>
          <OrderStatusBar
            orderStatus={unifiedStatus}
            diningOptionBehavior={order.diningOptionBehavior}
            curbside={curbside}
            restaurantName={restaurantName}
            restaurantNumber={order.restaurant.location.phone}
            pickupInstructions={order.guestCommunication}
            completedDate={orderCompletedDate}
            driverName={driverInfo?.driverName}
          />
          {orderStatusInfo?.trackingDetails?.dropOffImageUrl &&
            orderStatusInfo?.deliveryStatus ===
              ThirdPartyDeliveryStatus.OrderDelivered && (
              <DeliveryPhotoButton
                deliveryImageUrl={
                  orderStatusInfo.trackingDetails.dropOffImageUrl
                }
              />
            )}
          {showMap && (
            <div data-testid='tracker-map' className='mapWrapper'>
              <OrderTrackerMap
                order={order}
                orderStatusData={orderStatusInfo}
              />
            </div>
          )}
          {showFulfillmentInfo && (
            <TrackerFulfillmentInfo
              order={order}
              orderStatus={unifiedStatus}
              orderStatusData={orderStatusInfo}
              isUnderHourAfterDelivery={isUnderHourAfterDelivery}
            />
          )}
        </div>
        {showDriverInfo && (
          <div className='sectionBlock'>
            <DeliveryDriverInfo driverInfo={driverInfo} />
          </div>
        )}
      </div>
    )
  }

  return <div data-testid='order-tracker'>{body}</div>
}
